import axios from 'axios';

// Identity Pixel admin (super_admin only). Proxied by the Data Bodies backend
// to the pixel service's private admin API: /api/admin/pixels/*.
const api = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const MASK = '__keep__';

export type CustomAuth =
  | { type: 'none' }
  | { type: 'bearer'; token: string }
  | { type: 'basic'; username: string; password: string }
  | { type: 'header'; name: string; value: string };

export type PixelDestination =
  | { type: 'none' }
  | { type: 'data_body'; dataBodyId?: string; dataBodyName?: string; webhookUrl?: string }
  | {
      type: 'custom_api'; url: string; method: 'POST' | 'PUT' | 'PATCH';
      headers: Record<string, string>; auth: CustomAuth;
      fieldMap: { from: string; to: string; required?: boolean }[]; staticBody: Record<string, string>; timeoutMs?: number;
    };

export interface PixelStats {
  pings: number; events_stored: number; unique_visitors: number; bots: number; gpc_optouts: number;
  rejected_domain: number; rejected_sensitive: number; throttled: number; identify_events: number; lookups: number;
  matches_email: number; matches_phone: number; matches_md5: number; matches_visitor: number; matches_ip: number;
  no_match: number; dup_suppressed: number; below_confidence: number; delivered: number; delivery_failed: number;
  matches: number; match_rate: number;
}

export interface PixelInput {
  name: string;
  env?: 'test' | 'live';
  allowedDomains: string[];
  staticAttributes: Record<string, string>;
  attributeAllowlist: string[];
  formCapture: boolean;
  ipMatching: boolean;
  dedupeDays: number;
  minConfidence: number;
  sensitivePatterns: string[];
  /** Canonical graph attributes to pass; null = the default core set. */
  passAttributes: string[] | null;
  destination: PixelDestination;
  notes: string | null;
}

export interface PixelRow extends PixelInput {
  id: string;
  env: 'test' | 'live';
  publicKey: string;
  status: 'active' | 'inactive';
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lastPingAt?: string | null;
  stats?: { today: PixelStats; d7: PixelStats; d30: PixelStats };
}

export interface PixelReport {
  pixel: PixelRow;
  snippet: string;
  daily: Array<Record<string, any>>;
  recentMatches: Array<Record<string, any>>;
  recentAttempts: Array<Record<string, any>>;
  topPages: Array<{ page: string; hits: number }>;
  recentEvents: Array<Record<string, any>>;
}

export interface DataBodyOption { id: string; name: string; status: string; org_name: string | null }

export async function listPixels(token: string): Promise<{ env: 'test' | 'live'; pixels: PixelRow[] }> {
  return (await api.get('/api/admin/pixels', auth(token))).data;
}
export async function getPixelMeta(token: string): Promise<{ env: 'test' | 'live'; publicOrigin: string; payloadFields: string[]; coreFields?: string[]; metaFields?: string[] }> {
  return (await api.get('/api/admin/pixels/_meta', auth(token))).data;
}
export async function listPixelDataBodies(token: string): Promise<DataBodyOption[]> {
  return (await api.get('/api/admin/pixels/_data-bodies', auth(token))).data;
}
export async function createPixel(token: string, input: PixelInput): Promise<{ pixel: PixelRow; snippet: string }> {
  return (await api.post('/api/admin/pixels', input, auth(token))).data;
}
export async function updatePixel(token: string, id: string, input: Partial<PixelInput>): Promise<{ pixel: PixelRow }> {
  return (await api.patch(`/api/admin/pixels/${encodeURIComponent(id)}`, input, auth(token))).data;
}
export async function setPixelStatus(token: string, id: string, status: 'active' | 'inactive'): Promise<{ pixel: PixelRow }> {
  return (await api.post(`/api/admin/pixels/${encodeURIComponent(id)}/status`, { status }, auth(token))).data;
}
export async function rotatePixelKey(token: string, id: string): Promise<{ pixel: PixelRow; snippet: string }> {
  return (await api.post(`/api/admin/pixels/${encodeURIComponent(id)}/rotate-key`, {}, auth(token))).data;
}
export async function getPixelReport(token: string, id: string, days = 30): Promise<PixelReport> {
  return (await api.get(`/api/admin/pixels/${encodeURIComponent(id)}/report`, { ...auth(token), params: { days } })).data;
}
export async function testPixelDelivery(token: string, id: string): Promise<{ result: { ok: boolean; status: number | null; error: string | null; response: string | null; durationMs: number } }> {
  return (await api.post(`/api/admin/pixels/${encodeURIComponent(id)}/test-delivery`, {}, auth(token))).data;
}
